<template>
  <div class="wrong-questions-page">
    <AppBreadcrumb :items="breadcrumbItems" :showBack="true" @back="goBack">
      <template #actions>
        <div v-if="testMode && testQuestions.length > 0" class="test-header-actions">
          <a-tag color="orange" class="test-tag">
            <span class="tag-icon">❌</span>
            {{ currentIndex + 1 }} / {{ testQuestions.length }}
          </a-tag>

          <a-tag color="green" class="test-tag">
            <span class="tag-icon">✅</span>
            {{ Object.keys(selectedAnswers).length }} / {{ testQuestions.length }}
          </a-tag>

          <a-button 
            type="primary" 
            danger 
            size="small"
            @click="exitTestMode"
          >
            <template #icon>✕</template>
           Yakunlash
          </a-button>
        </div>
      </template>
    </AppBreadcrumb>

    <!-- TEST MODE -->
    <div v-if="testMode && testQuestions.length > 0" class="test-mode">
      <a-row :gutter="[16, 16]">
        <!-- Savol qismi -->
        <a-col :xs="24" :xl="12">
          <a-card class="custom-card question-card">
            <template #title>
              <div class="card-title">
                <span>Xato savollar testi</span>
                <a-tag color="orange">⚠️ Xato savol</a-tag>
              </div>
            </template>

            <!-- Oldingi xato javob -->
            <div v-if="currentQuestion?.previous_wrong_answer" class="wrong-info">
              <div class="wrong-answer-box">
                <span class="wrong-label">Siz xato belgilagansiz:</span>
                <span class="wrong-answer">{{ currentQuestion.previous_wrong_answer.text }}</span>
              </div>
            </div>

            <!-- Savol matni -->
            <h3 class="question-text">{{ currentQuestion?.text || 'Savol topilmadi' }}</h3>

            <!-- Variantlar -->
            <div class="options-list">
              <div
                v-for="(option, idx) in currentQuestion?.options || []"
                :key="option.id"
                class="option-item"
                :class="getOptionClass(option)"
                @click="selectAnswer(option)"
              >
                <span class="option-letter">{{ getOptionLetter(idx) }}</span>
                <span class="option-content">{{ option.text }}</span>
                <span v-if="selectedAnswers[currentQuestion?.id] === option.id" class="selected-check">✓</span>
              </div>
            </div>
          </a-card>
        </a-col>

        <!-- Rasm va tushuntirish qismi -->
        <a-col :xs="24" :xl="12">
          <a-card class="custom-card info-card">
            <!-- Rasm -->
            <div class="image-container">
              <img
                v-if="currentQuestion?.image_url"
                :src="currentQuestion.image_url"
                class="question-image"
                alt="Savol rasmi"
              />
              <div v-else class="no-image">
                <img src="../../public/no-image.png" alt="">
              </div>
            </div>

            <!-- Tushuntirish -->
            <div v-if="showExplanation" class="explanation-box">
              <div class="explanation-title">📝 Tushuntirish</div>
              <div class="explanation-content">{{ currentQuestion?.explanation || 'Tushuntirish mavjud emas' }}</div>
              
              <div v-if="isCurrentAnswerCorrect" class="correct-message">
                ✅ To'g'ri javob! Xato savollar ro'yxatidan o'chirildi.
              </div>
              <div v-else-if="isCurrentAnswerWrong" class="wrong-message">
                ❌ Xato javob. Qayta urinib ko'ring.
              </div>
            </div>

            <!-- Progress -->
            <div class="progress-section">
              <div class="progress-info">
                <span>Javob berildi: {{ Object.keys(selectedAnswers).length }} / {{ testQuestions.length }}</span>
                <span class="progress-percent">{{ Math.round((Object.keys(selectedAnswers).length / testQuestions.length) * 100) }}%</span>
              </div>
              <a-progress 
                :percent="Math.round((Object.keys(selectedAnswers).length / testQuestions.length) * 100)" 
                :show-info="false"
                strokeColor="#f59e0b"
                trailColor="#e8e8e8"
              />
            </div>

            <!-- Yakunlash tugmasi -->
            <a-button
              v-if="Object.keys(selectedAnswers).length === testQuestions.length"
              type="primary"
              block
              size="large"
              class="finish-button"
              :loading="finishLoading"
              @click="finishTest"
            >
              <template #icon>🏁</template>
              Testni yakunlash
            </a-button>
          </a-card>
        </a-col>
      </a-row>

      <!-- Savol raqamlari -->
      <div class="question-numbers">
        <div class="numbers-grid">
          <button
            v-for="(q, index) in testQuestions"
            :key="q.id"
            class="number-btn"
            :class="getNumberButtonClass(index)"
            @click="goToQuestion(index)"
          >
            {{ index + 1 }}
          </button>
        </div>
      </div>
    </div>

    <!-- LIST MODE (xato savollar ro'yxati) -->
    <div v-else class="list-mode">
      <a-spin :spinning="loading">
        <div v-if="wrongQuestions && wrongQuestions.length > 0">
          

          <!-- Test boshlash tugmasi -->
          <div class="start-test-section">
            <a-button 
              type="primary" 
              size="large" 
              class="start-test-btn"
              @click="startWrongTest"
              :loading="starting"
            >
              <template #icon>🚀</template>
              Xato savollarni ishlash ({{ totalWrongCount }} ta)
            </a-button>
            <p class="start-test-note">
              Barcha xato savollar random tartibda beriladi. To'g'ri topsangiz, avtomatik o'chib ketadi.
            </p>
          </div>
        </div>

        <div v-else class="empty-state">
          <div class="empty-icon">✅</div>
          <h3>Xato belgilangan savollar topilmadi</h3>
          <p>Siz hali xato belgilagan savollaringiz yo'q</p>
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppBreadcrumb from '@/components/AppBreadcrumb.vue'
import { message } from 'ant-design-vue'

export default {
  name: 'WrongQuestionsView',
  components: { AppBreadcrumb },

  data() {
    return {
      backendUrl: process.env.VUE_APP_API_URL || 'https://avtotesting.uz/api',
      loading: false,
      starting: false,
      finishLoading: false,
      wrongQuestions: [],
      groupedQuestions: [],
      expandedQuestion: null,
      
      // Test mode
      testMode: false,
      testQuestions: [],
      testResultId: null,
      testWrongIds: [],
      currentIndex: 0,
      selectedAnswers: {},
      showExplanation: false,
      startedAt: null,
      resolvedWrongQuestions: []
    }
  },

  computed: {
    breadcrumbItems() {
      return [{ label: 'Xato savollar' }]
    },

    totalWrongCount() {
      return this.wrongQuestions?.length || 0
    },

    testResultsCount() {
      return this.groupedQuestions?.length || 0
    },

    uniqueQuestionsCount() {
      if (!this.wrongQuestions || !this.wrongQuestions.length) return 0
      const uniqueIds = new Set(this.wrongQuestions.map(q => q.question_id))
      return uniqueIds.size
    },

    currentQuestion() {
      return this.testQuestions[this.currentIndex] || null
    },

    isCurrentAnswerCorrect() {
      if (!this.currentQuestion) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctOption = this.currentQuestion.options?.find(o => o.is_correct)
      return selectedId && correctOption && selectedId === correctOption.id
    },

    isCurrentAnswerWrong() {
      if (!this.currentQuestion) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctOption = this.currentQuestion.options?.find(o => o.is_correct)
      return selectedId && correctOption && selectedId !== correctOption.id
    }
  },

  mounted() {
    this.fetchWrongQuestions()
    window.addEventListener('keydown', this.handleKeyDown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('accessToken')
      return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    },

    getOptionLetter(index) {
      return String.fromCharCode(65 + index)
    },

    async fetchWrongQuestions() {
      this.loading = true
      try {
        const response = await axios.get(
          `${this.backendUrl}/api/v1/tests/wrong-questions/`,
          { headers: this.getAuthHeaders() }
        )
        
        if (response.data) {
          // API javobiga qarab ma'lumotlarni moslashtirish
          if (Array.isArray(response.data)) {
            this.wrongQuestions = response.data
            this.groupQuestionsByTest()
          } else if (response.data.questions) {
            this.wrongQuestions = response.data.questions
            this.groupQuestionsByTest()
          } else {
            this.wrongQuestions = []
            this.groupedQuestions = []
          }
        }
      } catch (error) {
        console.error('Xato savollarni olishda xatolik:', error)
        message.error('Xato savollarni olishda xatolik yuz berdi')
      } finally {
        this.loading = false
      }
    },

    // Savollarni testlar bo'yicha guruhlash
    groupQuestionsByTest() {
      if (!this.wrongQuestions || !this.wrongQuestions.length) {
        this.groupedQuestions = []
        return
      }

      const groups = {}
      
      this.wrongQuestions.forEach(q => {
        const testId = q.test_result_id || 'unknown'
        if (!groups[testId]) {
          groups[testId] = {
            test_id: testId,
            test_date: q.test_date || new Date().toISOString(),
            score: q.test_score || 0,
            questions: []
          }
        }
        groups[testId].questions.push(q)
      })

      this.groupedQuestions = Object.values(groups)
    },

    async startWrongTest() {
      if (this.starting) return
      
      this.starting = true
      try {
        const response = await axios.post(
          `${this.backendUrl}/api/v1/tests/wrong-questions-start/`,
          {},
          { headers: this.getAuthHeaders() }
        )

        console.log('API javobi:', response.data)

        if (response.data && response.data.questions) {
          this.testQuestions = response.data.questions
          this.testResultId = response.data.result?.id
          this.testWrongIds = response.data.meta?.wrong_question_ids || []
          
          // Test mode ni yoqish
          this.testMode = true
          this.currentIndex = 0
          this.selectedAnswers = {}
          this.showExplanation = false
          this.startedAt = Date.now()
          this.resolvedWrongQuestions = []
          
          console.log('Test boshlandi:', this.testQuestions.length, 'ta savol')
          message.success(`${this.testQuestions.length} ta xato savol bilan test boshlandi`)
        } else {
          message.warning('Test boshlash uchun xato savollar topilmadi')
        }
      } catch (error) {
        console.error('Test boshlashda xatolik:', error)
        message.error(
          error?.response?.data?.detail || 'Test boshlashda xatolik yuz berdi'
        )
      } finally {
        this.starting = false
      }
    },

    exitTestMode() {
      this.testMode = false
      this.testQuestions = []
      this.testResultId = null
      this.testWrongIds = []
      this.selectedAnswers = {}
      this.showExplanation = false
    },

    handleKeyDown(e) {
      if (!this.testMode || !this.currentQuestion) return
      
      const key = e.key
      if (key >= '1' && key <= '6') {
        const index = parseInt(key) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option && !this.selectedAnswers[this.currentQuestion.id]) {
          this.selectAnswer(option)
        }
      } else if (key === 'F1' || key === 'F2' || key === 'F3' || key === 'F4' || key === 'F5' || key === 'F6') {
        e.preventDefault()
        const index = parseInt(key.substring(1)) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option && !this.selectedAnswers[this.currentQuestion.id]) {
          this.selectAnswer(option)
        }
      }
    },

    selectAnswer(option) {
      if (!this.testMode || !this.currentQuestion || this.selectedAnswers[this.currentQuestion.id]) {
        return
      }

      console.log('Javob tanlandi:', option)

      // Vue 3 da to'g'ridan-to'g'ri o'zgartirish
      this.selectedAnswers[this.currentQuestion.id] = option.id
      this.showExplanation = true

      // Agar to'g'ri javob bo'lsa
      if (option.is_correct) {
        const wrongQuestionId = this.currentQuestion.wrong_question_id
        if (wrongQuestionId && !this.resolvedWrongQuestions.includes(wrongQuestionId)) {
          this.resolvedWrongQuestions.push(wrongQuestionId)
          this.resolveWrongQuestion(wrongQuestionId, option.id)
        }
      }

      // 1.5 soniyadan keyin keyingi savolga o'tish
      setTimeout(() => {
        if (this.currentIndex < this.testQuestions.length - 1) {
          this.currentIndex++
          this.showExplanation = !!this.selectedAnswers[this.testQuestions[this.currentIndex]?.id]
        }
      }, 1500)
    },

    async resolveWrongQuestion(wrongQuestionId, selectedOptionId) {
      try {
        await axios.post(
          `${this.backendUrl}/api/v1/tests/wrong-question-resolve/${wrongQuestionId}/`,
          { selected_option_id: selectedOptionId },
          { headers: this.getAuthHeaders() }
        )
      } catch (error) {
        console.error('Xato savolni o\'chirishda xatolik:', error)
      }
    },

    getOptionClass(option) {
      if (!this.currentQuestion) return []
      
      const classes = ['option-item']
      const selectedId = this.selectedAnswers[this.currentQuestion?.id]
      const correctOption = this.currentQuestion?.options?.find(o => o.is_correct)

      if (selectedId) {
        if (option.id === selectedId) {
          classes.push('selected')
          if (option.is_correct) {
            classes.push('correct')
          } else {
            classes.push('wrong')
          }
        }
        if (option.is_correct && selectedId !== correctOption?.id) {
          classes.push('show-correct')
        }
      }

      return classes
    },

    getNumberButtonClass(index) {
      const classes = ['number-btn']
      const question = this.testQuestions[index]
      
      if (this.currentIndex === index) {
        classes.push('active')
      }
      
      if (question && this.selectedAnswers[question.id]) {
        const correctOption = question.options?.find(o => o.is_correct)
        if (this.selectedAnswers[question.id] === correctOption?.id) {
          classes.push('correct')
        } else {
          classes.push('wrong')
        }
      }
      
      return classes
    },

    goToQuestion(index) {
      this.currentIndex = index
      this.showExplanation = !!this.selectedAnswers[this.testQuestions[index]?.id]
    },

    buildAnswersPayload() {
      return this.testQuestions.map(q => ({
        question_id: q.id,
        selected_option_id: this.selectedAnswers[q.id] || null
      }))
    },

    async finishTest() {
      if (this.finishLoading) return

      this.finishLoading = true
      try {
        const durationSeconds = Math.floor((Date.now() - this.startedAt) / 1000)

        await axios.post(
          `${this.backendUrl}/api/v1/tests/finish/`,
          {
            result_id: this.testResultId,
            duration_seconds: durationSeconds,
            answers: this.buildAnswersPayload()
          },
          { headers: this.getAuthHeaders() }
        )

        message.success('Test yakunlandi')
        this.exitTestMode()
        this.fetchWrongQuestions()
      } catch (error) {
        console.error('Testni yakunlashda xatolik:', error)
        message.error('Testni yakunlashda xatolik yuz berdi')
      } finally {
        this.finishLoading = false
      }
    },

    toggleExplanation(id) {
      this.expandedQuestion = this.expandedQuestion === id ? null : id
    },

    formatDate(dateString) {
      if (!dateString) return 'Noma\'lum sana'
      const date = new Date(dateString)
      return date.toLocaleDateString('uz-UZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getScoreColor(score) {
      if (score >= 80) return 'green'
      if (score >= 60) return 'blue'
      if (score >= 40) return 'orange'
      return 'red'
    },

    goBack() {
      if (this.testMode) {
        this.exitTestMode()
      } else {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
.wrong-questions-page {
  min-height: 100vh;
  padding: 20px;
  background: var(--bg);
}

/* Test mode styles */
.test-mode {
  margin-top: 20px;
  padding-bottom: 80px;
}

.test-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.test-tag {
  font-size: 14px;
  padding: 4px 12px;
}

.tag-icon {
  margin-right: 4px;
}

.question-card, .info-card {
  height: fit-content;
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.wrong-info {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--danger);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
}

.wrong-answer-box, .correct-answer-box {
  margin-bottom: 8px;
}

.wrong-label {
  color: var(--danger);
  font-weight: 600;
  margin-right: 8px;
}

.wrong-answer {
  color: var(--text);
}

.correct-label {
  color: var(--success);
  font-weight: 600;
  margin-right: 8px;
}

.correct-answer {
  color: var(--success);
  font-weight: 600;
}

.question-text {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
  line-height: 1.5;
  color: var(--text);
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-item {
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;
  background: var(--bg-card);
  position: relative;
}

.option-item:hover {
  border-color: var(--primary);
  transform: translateX(4px);
}

.option-item.selected {
  border-width: 2px;
}

.option-item.selected.correct {
  border-color: var(--success);
  background: rgba(34, 197, 94, 0.1);
}

.option-item.selected.wrong {
  border-color: var(--danger);
  background: rgba(239, 68, 68, 0.1);
}

.option-item.show-correct {
  border-color: var(--success);
  background: rgba(34, 197, 94, 0.05);
}

.option-letter {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  border-radius: 6px;
  font-weight: 600;
}

.option-content {
  flex: 1;
  color: var(--text);
}

.selected-check {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--success);
  color: white;
  border-radius: 50%;
  font-size: 14px;
}

.image-container {
  width: 100%;
  height: 300px;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
  margin-bottom: 20px;
}

.question-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.no-image {
  color: var(--text-soft);
  font-size: 16px;
}

.explanation-box {
  padding: 16px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid var(--primary);
  border-radius: 8px;
  margin-bottom: 20px;
}

.explanation-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 8px;
}

.explanation-content {
  color: var(--text);
  line-height: 1.6;
  font-size: 14px;
}

.correct-message {
  margin-top: 12px;
  padding: 10px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid var(--success);
  border-radius: 6px;
  color: var(--success);
  font-weight: 600;
}

.wrong-message {
  margin-top: 12px;
  padding: 10px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--danger);
  border-radius: 6px;
  color: var(--danger);
  font-weight: 600;
}

.progress-section {
  margin-top: 20px;
  padding: 16px;
  background: var(--bg-card);
  border-radius: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: var(--text);
}

.progress-percent {
  font-weight: 600;
  color: var(--primary);
}

.finish-button {
  margin-top: 20px;
  height: 48px;
  font-size: 16px;
}

.question-numbers {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg);
  border-top: 1px solid var(--border);
  padding: 16px;
  z-index: 100;
}

.numbers-grid {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px;
  max-width: 1200px;
  margin: 0 auto;
}

.number-btn {
  min-width: 40px;
  height: 40px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  color: var(--text);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.number-btn:hover {
  border-color: var(--primary);
  transform: translateY(-2px);
}

.number-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.number-btn.correct {
  background: var(--success);
  color: white;
  border-color: var(--success);
}

.number-btn.wrong {
  background: var(--danger);
  color: white;
  border-color: var(--danger);
}

/* List mode styles */
.stats-cards {
  margin: 24px 0;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
}

.stat-icon {
  font-size: 32px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.stat-icon.danger {
  background: rgba(239, 68, 68, 0.15);
  color: var(--danger);
}

.stat-icon.primary {
  background: rgba(59, 130, 246, 0.15);
  color: var(--primary);
}

.stat-icon.warning {
  background: rgba(245, 158, 11, 0.15);
  color: var(--warning);
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text);
}

.stat-label {
  font-size: 13px;
  color: var(--text-soft);
}

.start-test-section {
  text-align: center;
  margin: 32px 0;
  padding: 32px;
  background: linear-gradient(145deg, var(--bg-card), var(--bg-card-2));
  border: 1px solid var(--border);
  border-radius: 16px;
}

.start-test-btn {
  min-width: 300px;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
}

.start-test-note {
  margin-top: 12px;
  color: var(--text-soft);
  font-size: 14px;
}

.result-group {
  margin-bottom: 24px;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.result-date {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin-right: 12px;
}

.result-count {
  color: var(--text-soft);
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-item {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
}

.question-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.question-text {
  flex: 1;
  font-size: 14px;
  color: var(--text);
  min-width: 250px;
}

.question-number {
  display: inline-block;
  width: 24px;
  height: 24px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary);
  border-radius: 4px;
  text-align: center;
  line-height: 24px;
  font-size: 12px;
  font-weight: 600;
  margin-right: 8px;
}

.answers {
  display: flex;
  gap: 20px;
}

.answer {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.answer .label {
  font-size: 11px;
  color: var(--text-soft);
  text-transform: uppercase;
}

.answer .value {
  font-size: 13px;
  font-weight: 500;
}

.answer.wrong .value {
  color: var(--danger);
}

.answer.correct .value {
  color: var(--success);
}

.explanation-btn {
  padding: 4px 12px;
  background: none;
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--text);
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}

.explanation-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.explanation-image {
  max-width: 100%;
  max-height: 200px;
  margin-top: 12px;
  border-radius: 8px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@media (max-width: 768px) {
  .wrong-questions-page {
    padding: 12px;
  }
  
  .question-row {
    flex-direction: column;
  }
  
  .answers {
    flex-wrap: wrap;
  }
  
  .start-test-btn {
    min-width: 100%;
  }
  
  .numbers-grid {
    gap: 4px;
  }
  
  .number-btn {
    min-width: 35px;
    height: 35px;
    font-size: 12px;
  }
}

.numbers-grid::-webkit-scrollbar {
  height: 4px;
}

.numbers-grid::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 4px;
}
</style>